import { Button, Card } from 'components/atoms';
import { FormInput, LabeledStatus, ViewToggleButton } from 'components/molecules';
import ConditionalWarningPopupButton from 'components/molecules/buttons/conditional-warning-popup-button/ConditionalWarningPopupButton';
import ListWrapper from 'components/molecules/list-wrapper/ListWrapper';
import { QueryFilter, TopBar } from 'components/organisms';
import QueryFilterLabels from 'components/organisms/filters/query-form/labels/QueryFilterLabels';
import { TopbarPage } from 'components/templates';
import { useScroll } from 'components/templates/application/ApplicationTemplate';
import { useComponentVisible } from 'hooks';
import { useFormSave } from 'hooks/useFormSave';
import { useIsUserBanned } from 'hooks/useIsUserBanned';
import { useListViewById } from 'hooks/useListViewById';
import { useStoreState } from 'providers/StoreStateContext';
import { useTranslation } from 'react-i18next';
import { PiPlus } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { useGetTracksListInfinite } from 'services/tracks';
import { ITrackFilteredListParams } from 'services/tracks/types';
import { STATUS } from 'services/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { booleanSelectOptions, useEnumToSelectOptions } from 'utils/components/inputs/select';

const trackSearchInitialValues: ITrackFilteredListParams = {
  title: '',
  isrc: '',
  ean: '',
  artistNickname: '',
  artistName: '',
};

const Tracks = () => {
  const searchId = 'Tracks3';
  const viewId = 'tracks';

  const { searchById, setSearchById } = useStoreState();
  const filters = searchById[searchId] || trackSearchInitialValues;

  const tracks = useGetTracksListInfinite(filters);

  const scroll = useScroll({ fetchNextPage: tracks.fetchNextPage });

  const { t } = useTranslation();
  const navigate = useNavigate();
  const i18nextPrefix = 'track';
  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false);

  const { getSelectOptions } = useEnumToSelectOptions();

  const { isBanned } = useIsUserBanned();

  const { isAnyFormSaved } = useFormSave();
  const { clearForms } = useStoreState();
  const { view } = useListViewById(viewId, 'grid');

  return (
    <TopbarPage
      topbarAbsolute
      topbar={
        <TopBar
          title={t(`${i18nextPrefix}.title.list`)}
          customBackItem={<div />}
          topbarContent={
            <>
              <div className="flex">
                <ViewToggleButton id={viewId} />
                <QueryFilter.ButtonOpener
                  i18nextPrefix={i18nextPrefix}
                  onClick={handleComponentVisible}
                  variant={BUTTON_VARIANT.default}
                  isClear
                />
              </div>
              {!isBanned && (
                <ConditionalWarningPopupButton
                  title={t('popup.new-form-cancelation-title')}
                  message={t('popup.new-form-cancelation-description')}
                  confirmButtonVariant={BUTTON_VARIANT.background}
                  showWarning={isAnyFormSaved}
                  onConfirm={(a) => {
                    clearForms();
                    navigate('create/file', { replace: false });
                  }}
                  onlyIcon
                  variant={BUTTON_VARIANT.submit}
                >
                  <Button.Icon icon={<PiPlus />} />
                  <Button.Description description={t('button.add')} />
                </ConditionalWarningPopupButton>
              )}
            </>
          }
        >
          <QueryFilter<ITrackFilteredListParams>
            i18nextPrefix={i18nextPrefix}
            initialValues={filters}
          >
            <>
              <QueryFilter.Form<ITrackFilteredListParams>
                ref={ref}
                isOpen={isComponentVisible}
                onDebouncedFormChange={(values) => setSearchById(searchId, values)}
              >
                <QueryFilter.InputsGrid>
                  <FormInput.Select
                    appBackground
                    name="status"
                    label={t('input.label.status')}
                    options={getSelectOptions(STATUS, 'enum.STATUS')}
                  />
                  <FormInput.Text appBackground name="title" label={t('input.label.title')} />
                  <FormInput.Text appBackground name="isrc" label={t('input.label.isrc')} />
                  <FormInput.Text appBackground name="ean" label={t('input.label.ean')} />
                  <FormInput.Text
                    appBackground
                    name="artistNickname"
                    label={t('input.label.artistNickname')}
                  />
                  <FormInput.Text
                    appBackground
                    name="artistName"
                    label={t('input.label.artistName')}
                  />
                  <FormInput.Select
                    appBackground
                    name="hasDuplicatedArtist"
                    label={t('input.label.hasDuplicatedArtist')}
                    options={booleanSelectOptions}
                  />
                  <FormInput.Select
                    appBackground
                    name="hasContracts"
                    label={t('input.label.hasContracts')}
                    options={booleanSelectOptions}
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilterLabels scope="track" isFormOpen={isComponentVisible} />
            </>
          </QueryFilter>
        </TopBar>
      }
    >
      <ListWrapper
        view={view}
        items={tracks.data?.pages.flat()}
        isLoading={tracks.isLoading}
        isFetchingNextPage={tracks.isFetchingNextPage}
        item={(track) => (
          <Card
            view={view}
            key={track.id}
            onClick={() => navigate(`/track/${track.id}/file`)}
            audio={track.mp3PreviewLink ?? ''}
            header={track.mainTitle.title}
            description={track.mainTitle.version}
            playerId={track.id}
            status={<LabeledStatus status={track.trackStatus} noname />}
          />
        )}
      />
    </TopbarPage>
  );
};

export default Tracks;
